import { useQuery } from '@tanstack/react-query'
import { api } from './client'
import type { DayStatus, Envelope } from './types'

export interface DashboardEntry {
  uuid: string
  full_name: string
  branch: string | null
  check_in_at: string | null
  check_out_at: string | null
  worked_minutes: number
  overtime_minutes: number
  late_minutes: number
  status: DayStatus
}

export interface DashboardSummary {
  total_employees: number
  present: number
  late: number
  completed: number
  absent: number
  on_leave: number
  off_duty: number
}

export interface DashboardData {
  work_date: string
  branch: string | null
  summary: DashboardSummary
  present: DashboardEntry[]
  late: DashboardEntry[]
  completed: DashboardEntry[]
  absent: DashboardEntry[]
  on_leave: DashboardEntry[]
}

export interface DashboardFilters {
  branchId?: number
  date?: string
  /** 0 desactiva la actualización automática. */
  refreshMs?: number
}

export function useDashboard({ branchId, date, refreshMs = 30_000 }: DashboardFilters = {}) {
  return useQuery({
    queryKey: ['dashboard', branchId ?? null, date ?? null],
    queryFn: async (): Promise<DashboardData> => {
      const { data } = await api.get<Envelope<DashboardData>>('/admin/dashboard', {
        params: { branch_id: branchId, date },
      })
      return data.data
    },
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  })
}

/**
 * Grupo al que el backend asignó al empleado en la jornada.
 *
 * No coincide con `status`: quien ya marcó la salida sigue teniendo estado
 * `present` o `late`, pero pertenece al grupo `completed`. Filtrar por grupo
 * es lo que espera quien mira el panel.
 */
export type DashboardGroup = 'present' | 'late' | 'completed' | 'absent' | 'on_leave'

export interface DashboardRow extends DashboardEntry {
  group: DashboardGroup
}

/** Aplana los grupos del backend en una sola lista, conservando el grupo. */
export function flattenEntries(data: DashboardData): DashboardRow[] {
  const groups: DashboardGroup[] = ['late', 'present', 'completed', 'absent', 'on_leave']

  return groups.flatMap((group) =>
    data[group].map((entry) => ({ ...entry, group })),
  )
}
